import { useState } from "react";
import { ArrowLeft, ListTodo, AlertCircle } from "lucide-react";
import { AppShell } from "../../layouts/AppShell";
import { useActivities } from "../../../api/opsV2/hooks";
import type { ActivityWithContextOut } from "../../../api/opsV2/types";
import { ForbiddenPanel } from "../../components/ops/ForbiddenPanel";
import { NotAccessiblePanel } from "../../components/ops/NotAccessiblePanel";
import { OpsV2Error } from "../../../api/opsV2/client";

type MyWorkPageProps = {
  userName?: string;
  onBack?: () => void;
  onOpenProcessCase?: (engagementId: string, processCaseId: string, activityId?: string) => void;
};

function formatDue(dueAt: string | null): string {
  if (!dueAt) return "—";
  const d = new Date(dueAt);
  const now = new Date();
  if (d < now) return `En retard (${d.toLocaleDateString("fr-CA")})`;
  return d.toLocaleDateString("fr-CA");
}

export function MyWorkPage({
  userName = "Utilisateur",
  onBack,
  onOpenProcessCase,
}: MyWorkPageProps) {
  const [mine, setMine] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const { data: activities, loading, error, refetch } = useActivities({
    mine,
    status: statusFilter || undefined,
    limit: 100,
  });

  const is403 = error && (error as OpsV2Error).status === 403;
  const is404 = error && (error as OpsV2Error).status === 404;

  return (
    <AppShell
      userName={userName}
      topbar={
        <div className="flex items-center gap-4">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center gap-1 text-sm text-muted hover:text-text"
            >
              <ArrowLeft size={16} />
              Retour
            </button>
          )}
          <span className="text-sm font-medium text-text">Mes activités</span>
        </div>
      }
    >
      <div className="ds-container py-6">
        {is403 && (
          <ForbiddenPanel
            message="Vous n'avez pas accès aux activités."
            onBack={onBack ?? (() => window.history.back())}
          />
        )}
        {is404 && (
          <NotAccessiblePanel
            message="Ressource introuvable ou non accessible."
            onBack={onBack ?? (() => window.history.back())}
          />
        )}
        {!is403 && !is404 && (
          <>
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={mine}
                  onChange={(e) => setMine(e.target.checked)}
                  className="rounded border-border"
                />
                <span className="text-sm text-text">Assignées à moi</span>
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded border border-border bg-surface px-3 py-1.5 text-sm text-text"
              >
                <option value="">Tous les statuts</option>
                <option value="pending">En attente</option>
                <option value="active">Actif</option>
                <option value="blocked">Bloqué</option>
                <option value="done">Terminé</option>
              </select>
              <button
                type="button"
                onClick={() => refetch()}
                className="text-sm text-primary hover:underline"
              >
                Actualiser
              </button>
            </div>

            {loading && (
              <p className="text-sm text-muted">Chargement des activités…</p>
            )}
            {!loading && error && !is403 && !is404 && (
              <div className="flex items-center gap-2 text-danger text-sm">
                <AlertCircle size={16} />
                {(error as Error).message}
              </div>
            )}
            {!loading && !error && activities && (
              <>
                {activities.length === 0 ? (
                  <p className="text-sm text-muted">Aucune activité trouvée.</p>
                ) : (
                  <ul className="space-y-2">
                    {activities.map((a: ActivityWithContextOut) => (
                      <li key={a.id}>
                        <button
                          type="button"
                          onClick={() =>
                            onOpenProcessCase?.(a.ops_engagement_id, a.process_case_id, a.id)
                          }
                          className="w-full rounded-lg border border-border bg-surface p-4 text-left hover:border-primary/30 transition"
                        >
                          <div className="flex justify-between items-start gap-2">
                            <div>
                              <div className="font-medium text-text">{a.title}</div>
                              <div className="text-xs text-muted mt-1">
                                {a.engagement_title}
                                {a.client_name ? ` — ${a.client_name}` : ""} · {a.process_case_title ?? "Dossier"} · {a.step_title}
                              </div>
                            </div>
                            <div className="text-right shrink-0">
                              <span className="text-xs text-muted">{formatDue(a.due_at)}</span>
                              <span className="ml-2 inline-block rounded px-2 py-0.5 text-xs bg-surface-muted text-muted">
                                {a.status}
                              </span>
                            </div>
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}
